import { useCallback, useRef } from "react"

const STORAGE_KEY_PREFIX = "threshold-draft"

/**
 * Shape of the draft saved to localStorage.
 * Mirrors the editing state in InitiateChangeAccordion.
 */
export interface ThresholdDraft<TConfig, TThreshold> {
    title: string
    comment: string
    editableConfig: TConfig[]
    editableThresholds: TThreshold[]
    savedAt: number
}

/**
 * Builds the localStorage key scoped to a specific user.
 *
 * Parameters
 * ----------
 * username : string
 *     The Cognito sub (unique user identifier).
 *
 * Returns
 * -------
 * string
 *     localStorage key.
 */
const buildKey = (username: string): string =>
    `${STORAGE_KEY_PREFIX}-${username}`

/**
 * Maximum age (ms) for a draft to be considered restorable.
 * Matches the 10-minute lock duration.
 */
const MAX_DRAFT_AGE_MS = 10 * 60 * 1000

interface UseThresholdDraftReturn<TConfig, TThreshold> {
    saveDraft: (
        draft: Omit<ThresholdDraft<TConfig, TThreshold>, "savedAt">
    ) => void
    loadDraft: () => ThresholdDraft<TConfig, TThreshold> | null
    clearDraft: () => void
}

/**
 * Hook to persist and restore threshold editing drafts
 * via localStorage, scoped to the current user's sub ID.
 *
 * Drafts are automatically discarded if older than 10 minutes
 * (matching the server-side lock duration).
 *
 * Parameters
 * ----------
 * username : string
 *     Cognito sub ID used to scope the storage key.
 *
 * Returns
 * -------
 * UseThresholdDraftReturn
 *     saveDraft, loadDraft, and clearDraft functions.
 */
const useThresholdDraft = <TConfig, TThreshold>(
    username: string
): UseThresholdDraftReturn<TConfig, TThreshold> => {
    const keyRef = useRef(buildKey(username))
    keyRef.current = buildKey(username)

    const saveDraft = useCallback(
        (draft: Omit<ThresholdDraft<TConfig, TThreshold>, "savedAt">): void => {
            try {
                const payload: ThresholdDraft<TConfig, TThreshold> = {
                    ...draft,
                    savedAt: Date.now(),
                }
                localStorage.setItem(keyRef.current, JSON.stringify(payload))
            } catch {
                // localStorage may be full or unavailable
            }
        },
        []
    )

    const clearDraft = useCallback((): void => {
        try {
            localStorage.removeItem(keyRef.current)
        } catch {
            // Ignore removal failures
        }
    }, [])

    const loadDraft = useCallback((): ThresholdDraft<
        TConfig,
        TThreshold
    > | null => {
        try {
            const raw = localStorage.getItem(keyRef.current)
            if (!raw) return null
            const parsed = JSON.parse(raw) as ThresholdDraft<
                TConfig,
                TThreshold
            >
            if (Date.now() - parsed.savedAt > MAX_DRAFT_AGE_MS) {
                clearDraft()
                return null
            }
            return parsed
        } catch {
            clearDraft()
            return null
        }
    }, [clearDraft])

    return { saveDraft, loadDraft, clearDraft }
}

export default useThresholdDraft
